import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { UserInfoService } from '../../services/user-info.service';
import { DatePipe } from '@angular/common';
import { MemberLookup, WhbCrosswalk } from '../interfaces/member-lookup.interface';
import { PrePopulatedDataStateStructure } from '../configs/interfaces/prePopulatedDataStateStructure.interface';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { savePrepopulatedData } from '../configs/reducers/prePopulatedData.reducer';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';
import { saveCrossWalkInfo } from '../configs/reducers/whb-crosswalk.reducer';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { UserInfoStateStructure, UserInfo } from '@ptl/account-management';
import { MemberLookupResponse } from '../interfaces/member-lookup-response.interface';
import { DateService } from 'src/app/shared/services/date.service';

@Component({
    selector: 'app-member-lookup',
    templateUrl: './member-lookup.component.html',
    styleUrls: ['./member-lookup.component.scss'],
    standalone: false
})
export class MemberLookupComponent implements OnInit {
  @Input() isPlanChange: boolean;
  @Input() zipSegment: string;

  @Output() memberLookupResponse = new EventEmitter<MemberLookupResponse>();
  @Output() crosswalkResponse = new EventEmitter<WhbCrosswalk>();

  lookupForm: UntypedFormGroup;
  memberLookup: MemberLookup;
  userInfo: UserInfo;
  loginInfo: UserInfoStateStructure;
  showSpinner = false;
  showNoMemberFound = false;
  
  memberLookupRes: MemberLookupResponse = {
    memberFound: false,
    enableNext: false,
    isLoading: false
  };

  constructor(
    private fb: UntypedFormBuilder,
    private userInfoService: UserInfoService,
    private datePipe: DatePipe,
    private reducerUtilityService: ReducerUtilityService,
    private ngRedux: NgRedux<StateStructure>,
    private dateUtility: DateService,
    private commonUtilityService: CommonUtilityService
  ) {
    this.lookupForm = this.fb.group({
      umi: this.fb.control('', [Validators.required]),
      dob: this.fb.control(null, [Validators.required])
    });
  }

  ngOnInit() {
    this.prepopulateFormIfSaved();
  }

  async submit(form: UntypedFormGroup) {
    this.showSpinner = true;
    this.showNoMemberFound = false;

    try {
      this.memberLookupRes.isLoading = true;
      this.memberLookupResponse.emit({ ...this.memberLookupRes });
      this.memberLookup = null;

      const umi = this.lookupForm.controls['umi'].value;
      await this.saveCrosswalkData(umi);
      const dob = this.datePipe.transform(this.lookupForm.controls['dob'].value, 'MM/dd/yyyy');
      const res = await this.userInfoService.memberLookup(umi, dob, this.isPlanChange, true);
      this.memberLookup = res.payload.retrieveApplicationOutput;

      // Server can return a 200 with an empty object for some reason
      if (!this.memberLookup || !this.memberLookup.PERMZIP) {
        throw "Member information is empty";
      }

      await this.commonUtilityService.setBrandByZipCode(this.memberLookup.PERMZIP, this.memberLookup.PERMCOUNTYNAME, this.zipSegment);
      this.savePrepopulatedData(umi, dob);

      this.memberLookupRes.memberFound = true;
      this.memberLookupRes.enableNext = true;
    } 
    catch (e) {
      console.warn('member lookup service failed');
      this.showNoMemberFound = true;
      this.memberLookupRes.memberFound = false;
      this.memberLookupRes.enableNext = false;
    }
    finally {
      this.memberLookupRes.isLoading = false;
      this.memberLookupResponse.emit({ ...this.memberLookupRes });
    }

    this.showSpinner = false;
  }

  async saveCrosswalkData(umi: string) {
    if (!this.isPlanChange) {
      try {
        const whbCrosswalk: WhbCrosswalk = await this.userInfoService.doMedigapWHBCrosswalk(umi);
        this.ngRedux.dispatch(saveCrossWalkInfo(whbCrosswalk));
        this.crosswalkResponse.emit(whbCrosswalk);
      }
      catch (e) {
        console.warn('crosswalk service failed', e);
      }
    }
  }

  // save the prepopulated fields
  savePrepopulatedData(umi: string, dob: string) {
    const prePopulateData: PrePopulatedDataStateStructure = this.reducerUtilityService.getState('PrePopulateData');

    prePopulateData.prepopulatedFields.firstName = this.memberLookup.FIRSTNAME;
    prePopulateData.prepopulatedFields.middleInitial = this.memberLookup.MIDDLEINITIAL;
    prePopulateData.prepopulatedFields.lastName = this.memberLookup.LASTNAME;
    prePopulateData.prepopulatedFields.addressLine1 = this.memberLookup.PERMSTREET1;
    prePopulateData.prepopulatedFields.addressLine2 = this.memberLookup.PERMSTREET2;
    prePopulateData.prepopulatedFields.city = this.memberLookup.PERMCITY;
    prePopulateData.prepopulatedFields.county = this.memberLookup.PERMCOUNTYNAME;
    prePopulateData.prepopulatedFields.state = this.memberLookup.PERMSTATE;
    prePopulateData.prepopulatedFields.personalPhoneNumber = this.memberLookup.HOMEPHONE;
    prePopulateData.prepopulatedFields.gender = this.memberLookup.GENDER;
    prePopulateData.prepopulatedFields.zipCode = this.memberLookup.PERMZIP;
    prePopulateData.prepopulatedFields.dob = this.dateUtility.getDateAsFormattedString(this.memberLookup.BIRTHDATE, 'MM/DD/YYYY');
    prePopulateData.prepopulatedFields.memberLookup = this.memberLookup;
    prePopulateData.prepopulatedFields.effectiveStartDate = this.dateUtility.getNullOrFormattedDate(this.memberLookup.EFFECTIVESTARTDATE, 'MM/DD/YYYY');
    prePopulateData.prepopulatedFields.effectiveEndDate = this.dateUtility.getNullOrFormattedDate(this.memberLookup.EFFECTIVEENDDATE, 'MM/DD/YYYY');
    prePopulateData.prepopulatedFields.memberIdNumber = umi;
    prePopulateData.prepopulatedFields.hicNumber = this.memberLookup.HICNUMBER;
    prePopulateData.prepopulatedFields.planName = this.memberLookup.PLANNAME;
    
    this.ngRedux.dispatch(savePrepopulatedData(prePopulateData.prepopulatedFields));
  }

  prepopulateFormIfSaved() {
    const prePopulateData: PrePopulatedDataStateStructure = this.reducerUtilityService.getState('PrePopulateData');

    // Determine if we have saved data by checking if a dob is populated
    if (prePopulateData.prepopulatedFields?.memberLookup?.BIRTHDATE) {
      this.memberLookup = prePopulateData.prepopulatedFields.memberLookup;

      this.lookupForm.controls.umi.setValue(prePopulateData.prepopulatedFields.memberIdNumber);
      this.lookupForm.controls.dob.setValue(new Date(prePopulateData.prepopulatedFields.dob));
    }
  }
}
